import { CreateUserEvent, ExternalProfileEvent, InterestResponse, NotificationsInterestsInfo, NotificationsInterestFollow } from "./types";

export const requestEmailCheck = async (url: string, email: string, callback: (emailExists: boolean) => void) => {
  try {
    const res = await fetch(url, {
      method: 'POST',
      body: JSON.stringify({ email }),
      headers: {
        'Content-Type': 'application/json',
        "x-csrf-token": window.AD.csrf_token
      },
    })

    if (res.status === 400) {
      callback(true);
      return;
    }

    if (res.ok) {
      const data = await res.json();
      callback(data !== null);
      return;
    }

    throw new Error("Email status unknown");
  } catch (e) {
    console.error(e);
    throw e;
  }
}

export const requestUserCreation = async (
  url: string,
  { email, full_name, occupation, years_of_experience, country_code, password, token }: CreateUserEvent
) => {
  if (!url) {
    console.error('No signup URL provided');
    return;
  }

  try {
    const signUpData = {
      full_name,
      email,
      password,
      ocupation: occupation,
      years_of_experience,
      country: country_code || window.AFD.countryCode,
      country_code: country_code || window.AFD.countryCode,
      consent: true,
      subscribed_at: null, // User hasn't subscribed to newsletters yet
      'g-recaptcha-response': token,
    }

    const res = await fetch(`${url}?site=${window.AD.site ?? "us"}`, {
      method: 'POST',
      body: JSON.stringify(signUpData),
      headers: {
        'Content-Type': 'application/json',
        "x-csrf-token": window.AD.csrf_token
      },
    })

    if (!res.ok) {
      throw new Error("User creation failed");
    }

    const data = await res.json();
    return data?.resource ? data.resource : data;
  } catch (e) {
    console.error("Error creating user", e);
    throw e;
  }
}

export const requestUserLogin = async (
  url: string,
  { email, password }: { email: string, password: string }
) => {
  if (!url) {
    console.error('No login URL provided');
    return;
  }

  try {
    const res = await fetch(url, {
      method: 'POST',
      body: JSON.stringify({ email, password }),
      headers: {
        'Content-Type': 'application/json',
        "x-csrf-token": window.AD.csrf_token
      },
    })

    if (!res.ok) {
      throw new Error("Login failed");
    }

    const data = await res.json();
    return data?.resource;
  } catch (e) {
    console.error("Error logging in", e);
    throw e;
  }
}

export const sendCompleteExternalProfile = async (
  url: string,
  values: ExternalProfileEvent
) => {
  const formData = Object.entries(values).reduce((f, val) => {
    f.append(...val);
    return f;
  }, new URLSearchParams());

  try {
    const res = await fetch(url, {
      method: 'POST',
      body: formData.toString(),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
      },
    })
    if (!res.ok) {
      throw new Error("External profile completion failed");
    }

    const data = await res.json();
    return data;
  } catch (e) {
    console.error("Error completing external profile", e);
    throw e;
  }
}


export const requestInterests = async (
  url: string
): Promise<InterestResponse> => {
  if (!url) {
    console.error('No interests URL provided');
    return;
  }

  const accessToken = window.AD.getAccessToken()

  try {
    const res = await fetch(url, {
      method: 'GET',
      headers: {
        "Authorization": `Bearer ${accessToken}`
      },
    })

    if (!res.ok) {
      throw new Error("Error fetching interests");
    }
    const data = await res.json();

    return data as InterestResponse;
  } catch (e) {
    console.error("error fetching interests", e);
    throw e;
  }
}

export const requestNotificationsInterests = async (
  url: string
): Promise<NotificationsInterestsInfo> => {
  if (!url) {
    console.error('No notifications interests URL provided');
    return;
  }

  try {
    const res = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })

    if (!res.ok) {
      throw new Error("Error fetching notifications interest information");
    }

    const data = await res.json();

    return data as NotificationsInterestsInfo;
  } catch (e) {
    console.error("error fetching notifications interests", e);
    throw e;
  }
}

export const interestsStepFinished = async (
  url: string,
  action_type: string,
  name: string
) => {
  if (!url) {
    console.error('No interest follow step finished URL provided');
    return;
  }

  try {
    const res = await fetch(url, {
      method: 'POST',
      body: JSON.stringify({ action_type, name }),
      headers: {
        'Content-Type': 'application/json',
        "Authorization": `Bearer ${window.AD.getAccessToken()}`
      },
    })

    if (!res.ok) {
      throw new Error("Error marking interest follow step as finished");
    }
    const data = await res.json();

    return data;
  } catch (e) {
    console.error("error marking interest follow step as finished", e);
    throw e;
  }
}

export const createInterestFollow = async (
  url: string,
  interest: NotificationsInterestsInfo
) => {
  if (!url) {
    console.error('No create interest follow URL provided');
    return;
  }

  try {
    const entity = interest.category;
    const following_id = interest.original_id;
    const site = window.AFD['site'];

    const res = await fetch(url, {
      method: 'POST',
      body: JSON.stringify({ entity, following_id, site }),
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${window.AD.getAccessToken()}`,
        'x-csrf-token': window.AD.csrf_token
      },
    })

    if (!res.ok) {
      throw new Error("Error creating interest follow");
    }
    const data = await res.json();

    return data as NotificationsInterestFollow;
  } catch (e) {
    console.error("error creating interest follow", e);
    throw e;
  }
}

export const subscribeToNewsletters = async (
  url: string
) => {
  if (!url) {
    console.error('No create interest follow URL provided');
    return;
  }

  const subscribedAt = new Date()

  try {
    const res = await fetch(url, {
      method: 'POST',
      body: JSON.stringify({
        'subscribed_at': subscribedAt,
        'user_id': window.AD.CurrentUser.id
      }),
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${window.AD.getAccessToken()}`,
      }
    })

    if (!res) {
      throw new Error("Error subscribing to newsletters");
    }

    const data = await res.json();
    return data
  } catch (e) {
    console.error("error subscribing to newsletters", e);
    throw e;
  }
}

export const requestResetPassword = async (
  url: string,
  email: string
) => {
  if (!url || !email) {
    console.error('No reset password URL or email provided');
    return;
  }

  try {
    const res = await fetch(url, {
      method: 'POST',
      body: JSON.stringify({ email }),
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${window.AD.getAccessToken()}`
      },
    })

    if (res.status === 400) {
      console.error("Invalid email");
      throw { message: "This email is not registered", status: 400 };
    } else if (!res.ok) {
      console.log("Response:", res)
      throw { message: "Reset password failed", status: res.status };
    }

    const data = await res.json();
    return data;
  } catch (e) {
    console.error("Error requesting password reset", e);
    throw e;
  }
}